if (!customElements.get('variant-selects')) {

  // The option change request in flight for each product section, shared by
  // its main picker and sticky bar copy. A newer change aborts the older
  // request, so a slow response can never put an earlier choice in the form.
  const variantRequests = {};
  // The choice each product section's buy box (price, media, the variant
  // in the form) was last rendered for: { options, variant }. A change the
  // page can't show puts it back in the pickers.
  const renderedChoices = {};
  // Combined listings: the product each section is switching to while that
  // request is in flight, { url, picker }. A change made meanwhile is for
  // that product too.
  const pendingProducts = {};
  // Combined listings: how long the pointer rests on another product's
  // value before its content is fetched (sweeping across the values
  // fetches nothing), and how long that fetch serves a switch.
  const PREFETCH_DELAY = 150;
  const PREFETCH_TTL = 30000;

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      // "Require size selection": set only when the page rendered with
      // options left unselected (product-information.liquid).
      this.requireSelection = this.hasAttribute('data-require-selection');
      this.addEventListener('change', this.onVariantChange);
      this.productWrapper = this.closest('.thb-product-detail');
      if (this.productWrapper) {
        this.productSlider = this.productWrapper.querySelector('.product-images') ? this.productWrapper.querySelector('.product-images') : this.productWrapper.querySelector('.product-quick-images');
        this.thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');
        this.hideVariants = this.productSlider.dataset.hideVariants === 'true';
      }

      this.addEventListener('mousedown', (e) => {
        if (e.target.tagName === 'LABEL') {
          const scrollY = window.scrollY;
          requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
        }
      });
    }

    connectedCallback() {
      // Quick view's drawer content, when this picker is in it.
      this.drawerContent = this.closest('#Product-Drawer-Content');
      this.updateOptions();
      this.updateMasterId();
      if (!this.sticky) this.setRenderedChoice(this.currentVariant);
      this.setDisabled();
      this.setImageSet();
      this.prefetchProductUrls();

      if (this.requireSelection && !this.sticky) {
        // The browser can restore a size picked before a reload or Back.
        // Finish that selection once the sticky bar's copy is upgraded too.
        window.setTimeout(() => {
          this.updateOptions();
          if (this.isComplete()) this.onVariantChange();
        });
      }
    }

    // The variant pickers of this product section: the main one and the
    // sticky bar's copy. Other products on the page (featured product, quick
    // view) are never touched.
    get other() {
      return Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this && selector.dataset.section === this.dataset.section;
      });
    }

    // The cart line quick view is editing (QuickView.editing, app.js), when
    // this picker is in the product drawer; otherwise null.
    getLineEdit() {
      const quickView = customElements.get('quick-view');
      return quickView && quickView.editingFor ? quickView.editingFor(this) : null;
    }

    // The add to cart buttons of this section's main form and sticky bar.
    getAddButtons() {
      return document.querySelectorAll(`product-form[data-section="${this.dataset.section}"] .single-add-to-cart-button`);
    }

    // True when every option has a value: a checked radio, or a dropdown on
    // a real value rather than the "Choose options" placeholder.
    isComplete() {
      return Array.from(this.querySelectorAll('fieldset')).every((fieldset) => {
        const select = fieldset.querySelector('select');
        if (select) return select.value !== '';
        return !fieldset.querySelector('input') || !!fieldset.querySelector('input:checked');
      });
    }

    onVariantChange(event) {
      // The input or select changed: its value can belong to another
      // product of a combined listing (getProductUrl).
      this.changedInput = event ? event.target : null;
      this.updateOptions();
      if (this.requireSelection && !this.isComplete()) {
        this.onIncompleteChange();
        return;
      }
      this.toggleAddButton(true, '', false);
      this.removeErrorMessage();
      this.updateVariantText();

      // Dispatches product:variant-change once the response names the variant.
      this.renderVariantInfo();

      this.updateOther();
    }

    // Its markup was swapped out: quick view closing or opening another
    // product, a combined listing switch, the theme editor. Its request in
    // flight is dropped, so the reply never reaches the markup that took
    // its place, which has the same section id.
    disconnectedCallback() {
      const section = this.dataset.section;
      const request = variantRequests[section];
      if (request && request.picker === this) {
        request.abort();
        delete variantRequests[section];
      }
      const pending = pendingProducts[section];
      if (pending && pending.picker === this) delete pendingProducts[section];
      clearTimeout(this.prefetchTimer);
    }

    // Starts this section's option change request, aborting the one before,
    // and a combined listing switch still on its way with it (switchProduct
    // sets its own again).
    startRequest() {
      const section = this.dataset.section;
      if (variantRequests[section]) variantRequests[section].abort();
      delete pendingProducts[section];
      const request = new AbortController();
      request.picker = this;
      variantRequests[section] = request;
      return request;
    }

    // "Require size selection" with an option still unchosen: show the
    // chosen colour's media and price and keep the buy buttons waiting. The
    // id input, URL and quantity keep their server-rendered state, so no
    // default variant is put in the form.
    onIncompleteChange() {
      this.toggleAddButton(true, this.dataset.chooseLabel);
      this.setPaymentInert(true);
      this.removeErrorMessage();
      this.updateVariantText();
      this.setRenderedChoice(null);
      this.renderSelectionMedia();
      this.updateOther();
    }

    setRenderedChoice(variant) {
      renderedChoices[this.dataset.section] = { options: this.options.slice(), variant: variant };
    }

    // An option change the page couldn't show: the pickers go back to the
    // choice the buy box shows, so they agree with the price, the media and
    // the variant in the form, and the add button works again for it. What
    // follows the picker (product:variant-change: Complete the look, the
    // gift card form) hears that this variant is the one again.
    restoreRenderedChoice() {
      const choice = renderedChoices[this.dataset.section];
      if (!choice) return;
      this.setOptions(choice.options);
      this.updateOther();
      this.currentVariant = choice.variant;
      const idInput = document.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
      if (choice.variant && choice.variant.available && idInput && idInput.value === String(choice.variant.id)) {
        this.toggleAddButton(false);
      }
      this.showErrorMessage(window.theme.variantStrings.loadError);
      if (choice.variant && choice.variant.id && (!this.requireSelection || this.isComplete())) {
        dispatchCustomEvent('product:variant-change', {
          variant: choice.variant,
          variantTitle: this.options.join(' / '),
          sectionId: this.dataset.section
        });
      }
    }

    // The option change's request failed. With an answer (an HTTP error, or
    // a page without this section) on the product page, that page loads
    // `url` in full, which renders the choice. Without one (offline), or in
    // quick view and a featured product, which would leave their page, the
    // choice shown before comes back. A request a newer change aborted has
    // nothing to undo.
    onRequestFailure(error, url, request) {
      if (isAbortError(error) || request.signal.aborted || !this.isConnected) return;
      console.error(error);
      if (error.status && this.updateUrl) {
        window.location.assign(url);
        return;
      }
      this.restoreRenderedChoice();
    }

    // An unchosen option ("Require size selection") reads as ''.
    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.fieldsets.forEach((fieldset, i) => {
        if (fieldset.querySelector('select')) {
          this.options.push(fieldset.querySelector('select').value);
        } else if (fieldset.querySelectorAll('input').length) {
          const checked = fieldset.querySelector('input:checked');
          this.options.push(checked ? checked.value : '');
        }
      });
    }
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          label.textContent = this.options[i] || '';
        }
      });
    }
    updateMasterId() {
      const span = this.querySelector(`#SelectedVariant-${this.dataset.section}`);
      if (!span) return;
      this.currentVariant = {
        id: parseInt(span.dataset.variantId, 10) || null,
        available: span.dataset.available === 'true',
        title: span.dataset.variantTitle,
        price: span.dataset.variantPrice,
        featured_media: span.dataset.featuredMediaId ? { id: parseInt(span.dataset.featuredMediaId, 10) } : null
      };
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      const other = this.other[0];
      if (other) {
        other.setOptions(this.options);
        other.updateMasterId();
        other.setDisabled();
      }
    }

    // Checks these option values in this picker.
    setOptions(options) {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      options.forEach((option, i) => {
        const fieldset = fieldsets[i];
        if (!fieldset) return;
        const select = fieldset.querySelector('select');
        if (select) {
          select.value = option;
        } else {
          // An empty option unchecks the whole group.
          fieldset.querySelectorAll('input').forEach((input) => {
            input.checked = input.value === option;
          });
        }
      });
      this.updateOptions();
      this.updateVariantText();
    }

    updateMedia(variant = this.currentVariant) {
      if (!variant) return;
      if (!variant.featured_media) return;
      if (!this.productSlider) return;
      let mediaId = `#Slide-${this.dataset.section}-${variant.featured_media.id}`;
      let activeMedia = this.productSlider.querySelector(mediaId);

      if (this.thumbnails) {
        this.setActiveMediaSlider(mediaId, `#Thumb-${this.dataset.section}-${variant.featured_media.id}`, this.productSlider);
      } else {
        this.setActiveMedia(activeMedia);
      }

    }
    setActiveMedia(activeMedia) {

      this.productSlider.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      this.setImageSetMedia();

      activeMedia.classList.add('is-active');

      activeMedia.parentElement.prepend(activeMedia);

      if (!this.sticky) {
        // Scroll the gallery container to the start so the new active media is
        // visible. The window's scroll position is preserved by the surrounding
        // renderVariantInfo() flow — do not scroll the window here.
        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({
            left: 0,
            behavior: 'instant'
          });
        });
      }
    }
    setActiveMediaSlider(mediaId, thumbId, productSlider) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);

      if (flkty && this.hideVariants) {
        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (this.thumbnails) {
          if (this.thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            this.thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(this.thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        this.setImageSetMedia();

        if (this.thumbnails) {
          let activeThumb = this.thumbnails.querySelector(thumbId);

          activeThumb.classList.add('is-active');
          activeThumb.classList.add('is-initial-selected');
        }

        productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId);

      } else if (flkty) {
        productSlider.selectCell(mediaId);
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    // What this picker's product renders in: quick view's drawer content,
    // or its section. A product page, a featured product and quick view
    // each have an installments form and pickup availability.
    getProductContainer() {
      return this.drawerContent || this.closest('.shopify-section') || document;
    }

    updateVariantInput() {
      const productForms = this.getProductContainer().querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = this.getProductContainer().querySelector('.pickup-availability-wrapper');
      if (!pickUpAvailability) return;
      pickUpAvailability.update(this.currentVariant);
    }

    removeErrorMessage() {
      this.showErrorMessage(false);
    }

    // On the form beside this picker: the sticky bar's own form when the
    // sticky picker changed, otherwise the section's main form (quick view
    // has no <section> around it). The other form is cleared.
    showErrorMessage(message) {
      const sticky = this.dataset.sticky === '1';
      document.querySelectorAll(`product-form[data-section="${this.dataset.section}"]`).forEach((productForm) => {
        productForm.handleErrorMessage((productForm.dataset.sticky === '1') === sticky ? message : false);
      });
    }

    // The parts of the section that describe the variant, each re-rendered
    // from the response after an option change. Notify me, volume pricing
    // and model and fit keep their wrapper on the page even when empty
    // (product-notify-me, product-quantity-pricing, product-model-fit), so
    // a variant they have something for fills it in. Volume pricing follows
    // the quantity selector, whose new input it reads as it connects.
    getSectionsToRender() {
      const section = this.dataset.section;
      return [`price-${section}`, `price-${section}--sticky`, `product-image-${section}--sticky`, `inventory-${section}`, `sku-${section}`, `quantity-${section}`, `notify-me-${section}`, `quantity-pricing-${section}`, `model-fit-${section}`, `delivery-estimate-${section}`];
    }

    // The value ids chosen in each option. With fieldset and valueId, that
    // option counts as set to that value instead (a value being hovered).
    getSelectedValueIds(fieldset, valueId) {
      return this.fieldsets.map((el) => {
        if (el === fieldset) return valueId;
        if (el.querySelector('select')) {
          return el.querySelector('select').selectedOptions[0]?.dataset.valueId;
        }
        return el.querySelector('input:checked')?.dataset.valueId;
      }).filter(Boolean);
    }

    // The option value ids a combined listing switch asks the other product
    // for (fieldset and valueId as above). While "Require size selection"
    // still waits on an option, none: the server would pick a value for
    // it, and the other product renders with it left to choose instead.
    getSwitchValueIds(fieldset, valueId) {
      const ids = this.getSelectedValueIds(fieldset, valueId);
      return this.requireSelection && ids.length < this.fieldsets.length ? [] : ids;
    }

    // The request for productUrl with these option value ids chosen: its
    // section, or with page, its whole product page. Outside the product
    // page it asks for the quick-view view, where a featured product
    // renders the product asked for rather than the one it is set to
    // (featured-product.liquid): after a combined listing switch they
    // differ.
    getRequestUrl(productUrl, ids, page) {
      const params = [];
      if (ids.length) params.push(`option_values=${ids.join(',')}`);
      if (!page) params.push(`section_id=${this.dataset.section}`);
      if (!this.updateUrl) params.push('view=quick-view');
      return params.length ? `${productUrl}?${params.join('&')}` : productUrl;
    }

    // Combined listings: resting the pointer on a value of another product
    // fetches what choosing it would load, so switchProduct can show it at
    // once. Only a radio or swatch value, whose label is the one targeted:
    // a dropdown can't tell which of its options will be chosen.
    prefetchProductUrls() {
      // url -> { promise, time }: a fetch still on its way is shared too.
      this.prefetchCache = new Map();
      this.querySelectorAll('input[data-product-url]').forEach((input) => {
        const productUrl = input.dataset.productUrl;
        const fieldset = input.closest('fieldset');
        const label = input.nextElementSibling;
        if (!productUrl || productUrl === this.dataset.url || !fieldset || !label || label.tagName !== 'LABEL') return;

        label.addEventListener('mouseenter', () => {
          clearTimeout(this.prefetchTimer);
          this.prefetchTimer = setTimeout(() => {
            this.prefetch(this.getRequestUrl(productUrl, this.getSwitchValueIds(fieldset, input.dataset.valueId), this.updateUrl));
          }, PREFETCH_DELAY);
        });
        label.addEventListener('mouseleave', () => clearTimeout(this.prefetchTimer));
      });
    }

    prefetch(url) {
      if (this.getPrefetched(url)) return;
      const promise = this.fetchProduct(url);
      this.prefetchCache.set(url, { promise: promise, time: Date.now() });
      promise.catch(() => {
        const entry = this.prefetchCache.get(url);
        if (entry && entry.promise === promise) this.prefetchCache.delete(url);
      });
    }

    // The prefetch of url, while it is recent enough to show.
    getPrefetched(url) {
      const entry = this.prefetchCache && this.prefetchCache.get(url);
      return entry && Date.now() - entry.time < PREFETCH_TTL ? entry.promise : null;
    }

    // Combined listings: the product the changed value belongs to, when
    // that is another product than this picker's (product-option renders
    // its data-product-url). A value without one belongs to the product
    // shown, unless a switch is on its way: the change is then for the
    // product being switched to. Null when no switch is needed.
    getProductUrl() {
      const input = this.changedInput;
      let productUrl = '';
      if (input && this.contains(input)) {
        const el = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
        productUrl = (el && el.dataset.productUrl) || '';
      }
      const pending = pendingProducts[this.dataset.section];
      if (!productUrl && pending && pending.picker.isConnected) {
        productUrl = pending.url;
      }
      return productUrl && productUrl !== this.dataset.url ? productUrl : null;
    }

    // Combined listings: the chosen value belongs to another product of the
    // listing, which takes this one's place. The product page loads that
    // product's page and puts its main content and title in place, under
    // its URL. Quick view swaps the drawer's content and a featured product
    // its section, and neither touches the URL. A request that fails takes
    // onRequestFailure.
    switchProduct(productUrl) {
      const section = this.dataset.section;
      clearTimeout(this.prefetchTimer);
      const request = this.startRequest();
      const ids = this.getSwitchValueIds();
      const url = this.getRequestUrl(productUrl, ids, this.updateUrl);
      pendingProducts[section] = { url: productUrl, picker: this };
      // Only the newest request of this section clears it.
      const settle = () => {
        if (variantRequests[section] === request) delete pendingProducts[section];
      };
      // A newer option change has its own request, and a picker swapped out
      // (disconnectedCallback) has nothing left to update.
      const dropped = () => request.signal.aborted || !this.isConnected;

      this.loadProduct(url, request.signal)
        .then((content) => dropped() ? null : this.parseProduct(content, productUrl, url))
        // Only a failed request takes the failure path: an error while
        // rendering the reply is logged and nothing more.
        .then((content) => {
          if (dropped()) return;
          settle();
          this.showProduct(content, productUrl);
        }, (error) => {
          if (dropped()) return;
          settle();
          // A featured product whose section didn't render the other
          // product (no quick-view template) shows it on its own page.
          if (error.mismatch && !this.updateUrl && !this.drawerContent) {
            window.location.assign(ids.length ? `${productUrl}?option_values=${ids.join(',')}` : productUrl);
            return;
          }
          this.onRequestFailure(error, url, request);
        })
        .catch((e) => console.error(e));
    }

    // The response to a switch: its prefetch when there is a recent one,
    // otherwise a new request.
    loadProduct(url, signal) {
      return this.getPrefetched(url) || this.fetchProduct(url, signal);
    }

    // In quick view, the drawer's content, through quick view's own loader
    // and cache (app.js); otherwise the page or section's HTML.
    fetchProduct(url, signal) {
      const quickView = customElements.get('quick-view');
      if (this.drawerContent && quickView && quickView.load) {
        // Editing a cart line, what can be bought now (not the cache).
        return quickView.load(url, url, signal, !!this.getLineEdit());
      }
      return themeFetch(url, { signal: signal }).then((response) => response.text());
    }

    // What a switch puts in place, from its response: the product page's
    // main content and title, or this section. A response without it, or
    // a section that shows another product, fails like an HTTP error.
    parseProduct(content, productUrl, url) {
      if (this.drawerContent) return { html: content };
      const missing = () => Object.assign(new Error(`No product to switch to in ${url}`), { status: 200, mismatch: true });
      const html = new DOMParser().parseFromString(content, 'text/html');
      if (this.updateUrl) {
        const main = html.getElementById('main-content');
        if (!main) throw missing();
        return { html: main.innerHTML, title: html.title };
      }
      const newSection = html.getElementById(`shopify-section-${this.dataset.section}`);
      const span = newSection && newSection.querySelector(`#SelectedVariant-${this.dataset.section}`);
      if (!newSection || (span && span.dataset.productUrl !== productUrl.split('?')[0])) throw missing();
      return { html: newSection.innerHTML };
    }

    // Puts a switch's content in place, then gives back what the shopper
    // had: the scroll position, the sticky bar, and focus on the option
    // they changed. The new product's picker sends the events an option
    // change sends.
    showProduct(content, productUrl) {
      const drawerContent = this.drawerContent;
      const container = this.updateUrl ? document.getElementById('main-content') : (drawerContent || document.getElementById(`shopify-section-${this.dataset.section}`));
      if (!container) return;

      const scrollY = window.scrollY;
      const scrollTop = container.scrollTop;
      const focus = {
        sticky: this.sticky === '1',
        fieldset: this.changedInput ? this.changedInput.closest('fieldset') : null,
        id: this.changedInput ? this.changedInput.id : '',
        inPicker: this.contains(document.activeElement),
        inContainer: container.contains(document.activeElement)
      };
      const oldSticky = container.querySelector(`product-add-to-cart-sticky[data-section="${this.dataset.section}"]`);
      const stickyVisible = !!oldSticky && oldSticky.classList.contains('sticky--visible');
      // Quick view editing a cart line: the line, and the quantity chosen,
      // go on into the other product's form.
      const edit = drawerContent ? this.getLineEdit() : null;
      const quantityInput = edit && container.querySelector('input[name="quantity"]');
      const quantity = quantityInput ? quantityInput.value : edit && edit.quantity;

      replaceProductContent(container, content.html);
      if (edit) customElements.get('quick-view').applyEdit(container, { quantity: quantity, properties: true });

      // The new product's picker: on the product page, the one that keeps
      // the URL (a featured product can share the page).
      const picker = container.querySelector(this.updateUrl ? 'variant-selects[data-update-url="true"]:not([data-sticky])' : `#variant-selects-${this.dataset.section}`);
      const sticky = picker && container.querySelector(`product-add-to-cart-sticky[data-section="${picker.dataset.section}"]`);
      // The bar the shopper used stays in view, and open.
      if (sticky && stickyVisible) sticky.classList.add('sticky--visible');
      if (sticky && focus.sticky) sticky.open();

      if (this.updateUrl) {
        if (content.title) document.title = content.title;
        if (picker && picker.currentVariant && picker.currentVariant.id && !picker.requireSelection) {
          picker.updateURL();
        } else {
          window.history.replaceState({}, '', picker ? picker.dataset.url : productUrl);
        }
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        // Positions below the swapped content can have moved.
        if (typeof ScrollTrigger !== 'undefined') requestAnimationFrame(() => ScrollTrigger.refresh());
      } else if (drawerContent) {
        container.scrollTop = scrollTop;
      } else {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      }

      if (picker) {
        const focusPicker = focus.sticky && sticky ? sticky.querySelector('variant-selects') : picker;
        if (focus.inPicker && focus.fieldset && focusPicker) focusPicker.focusOption(focus.fieldset.dataset.index, focus.id);
        // The product page's own view event (theme.liquid) is for the
        // product it loaded with, and quick view's (app.js) for the one it
        // opened. A selection "Require size selection" still waits on has
        // no product:select, as after any option change.
        if (this.updateUrl) {
          picker.publishProductView('page');
        } else if (drawerContent) {
          picker.publishProductView('dialog');
        }
        if (!picker.requireSelection || picker.isComplete()) picker.publishProductSelect();
        if (picker.currentVariant && picker.currentVariant.id && !picker.requireSelection) {
          dispatchCustomEvent('product:variant-change', {
            variant: picker.currentVariant,
            variantTitle: picker.options.join(' / '),
            sectionId: picker.dataset.section
          });
        }
        if (this.updateUrl || (drawerContent && !edit)) addIdToRecentlyViewed(picker.dataset.productHandle, picker.dataset.productId);
      }
      // Focus was in the drawer's old content: the drawer keeps it.
      if (drawerContent && focus.inContainer && !container.contains(document.activeElement)) {
        const drawer = container.closest('#Product-Drawer');
        if (drawer) drawer.focus({ preventScroll: true });
      }
    }

    // Focuses the option at index (its fieldset's data-index): the input
    // with this id when it is the value chosen, otherwise the value chosen.
    focusOption(index, id) {
      const fieldset = this.querySelector(`fieldset[data-index="${index}"]`);
      if (!fieldset) return;
      const same = id ? document.getElementById(id) : null;
      const target = (same && fieldset.contains(same) && (same.tagName === 'SELECT' || same.checked) && same) ||
        fieldset.querySelector('select') || fieldset.querySelector('input:checked') || fieldset.querySelector('input');
      if (target) target.focus({ preventScroll: true });
    }

    renderVariantInfo() {
      const ids = this.getSelectedValueIds();
      if (!ids.length) return;

      // Combined listings: check if selected option points to a different product
      const productUrl = this.getProductUrl();
      if (productUrl) {
        this.switchProduct(productUrl);
        return;
      }

      const scrollY = window.scrollY;
      const request = this.startRequest();
      // The product page for this choice.
      const url = `${this.dataset.url}?option_values=${ids.join(',')}`;

      themeFetch(this.getRequestUrl(this.dataset.url, ids), { signal: request.signal })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          // Every render of this section has it.
          if (!html.getElementById(`SelectedVariant-${this.dataset.section}`)) {
            throw Object.assign(new Error(`No variant picker in ${url}`), { status: 200 });
          }
          return html;
        })
        // Only a failed request takes the failure path: an error while
        // rendering the reply is logged and nothing more.
        .then((html) => {
          // A newer option change has its own request, and a picker swapped out
          // (disconnectedCallback) has nothing left to update.
          if (request.signal.aborted || !this.isConnected) return;

          // 1. Resolve variant from hidden span
          const span = html.getElementById(`SelectedVariant-${this.dataset.section}`);
          if (span && parseInt(span.dataset.variantId, 10)) {
            this.currentVariant = {
              id: parseInt(span.dataset.variantId, 10),
              available: span.dataset.available === 'true',
              title: span.dataset.variantTitle,
              price: span.dataset.variantPrice,
              featured_media: span.dataset.featuredMediaId ? { id: parseInt(span.dataset.featuredMediaId, 10) } : null
            };
          } else {
            this.currentVariant = null;
          }

          // Combined listings server-side fallback: check if response is for a different product
          if (span && span.dataset.productUrl && span.dataset.productUrl !== this.dataset.url) {
            this.switchProduct(span.dataset.productUrl);
            return;
          }
          this.setRenderedChoice(this.currentVariant);

          // 2. Sync data-available onto current DOM inputs (drives setDisabled)
          this.syncAvailability(html);

          // 3. Update price, inventory, sku sections. Editing a cart line,
          // the quantity chosen stays: the quantity selector comes back at
          // the new variant's minimum.
          const edit = this.getLineEdit();
          const quantityInput = edit && document.getElementById(`Quantity-${this.dataset.section}`);
          const quantity = quantityInput ? quantityInput.value : null;
          this.getSectionsToRender().forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);
            if (source && destination) destination.innerHTML = source.innerHTML;
            if (id.includes('price') && destination) destination.classList.remove('visibility-hidden');
          });
          if (edit) customElements.get('quick-view').applyEdit(this.getProductContainer(), { quantity: quantity });

          this.publishProductSelect();

          // 4. Apply updated state. The payment buttons' inert wrapper
          // ("Require size selection") opens once a real variant is in the
          // form, and closes again for a combination that doesn't exist.
          if (!this.currentVariant) {
            this.toggleAddButton(true, '', true);
            this.setUnavailable();
            this.setPaymentInert(true);
            window.scrollTo({ top: scrollY, behavior: 'instant' });
            return;
          }
          this.setDisabled();
          this.updateVariantInput();
          if (this.updateUrl) this.updateURL();
          this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut);
          this.setPaymentInert(false);
          if (this.requireSelection) this.setSelectionPending(false);
          this.updateMedia();
          this.updatePickupAvailability();

          window.scrollTo({ top: scrollY, behavior: 'instant' });

          // Only now is the variant the one the shopper chose (the gift card
          // recipient form tracks its id).
          dispatchCustomEvent('product:variant-change', {
            variant: this.currentVariant,
            variantTitle: this.options.join(' / '),
            sectionId: this.dataset.section
          });
        }, (error) => this.onRequestFailure(error, url, request))
        .catch((e) => console.error(e));
    }

    // Copies each option value's data-available from a response. onlyIndexes
    // limits it to some options (their fieldsets' data-index).
    syncAvailability(html, onlyIndexes) {
      const sourceSelects = html.getElementById(`variant-selects-${this.dataset.section}`);
      if (sourceSelects) {
        sourceSelects.querySelectorAll('[data-value-id]').forEach((sourceEl) => {
          const vid = sourceEl.dataset.valueId;
          const avail = sourceEl.dataset.available;
          [this, ...this.other].forEach((el) => {
            const localEl = el.querySelector(`[data-value-id="${vid}"]`);
            if (!localEl) return;
            if (onlyIndexes && !onlyIndexes.includes(localEl.closest('fieldset').dataset.index)) return;
            localEl.dataset.available = avail;
          });
        });
      }
    }

    // Incomplete selection: ask for the values chosen so far, but use the
    // response only for the gallery, the price and the options still to
    // choose. The server fills those in with a default (eg. the first
    // available size), so the chosen colour's own availability, which
    // product-option rendered against any size, is left as it is.
    renderSelectionMedia() {
      const ids = this.getSelectedValueIds();
      if (!ids.length) return;

      // Combined listings: a colour can point to a different product.
      const productUrl = this.getProductUrl();
      if (productUrl) {
        this.switchProduct(productUrl);
        return;
      }

      const request = this.startRequest();
      const unchosen = this.fieldsets.filter((fieldset, i) => !this.options[i]);
      const unchosenIndexes = unchosen.map((fieldset) => fieldset.dataset.index);

      themeFetch(this.getRequestUrl(this.dataset.url, ids), { signal: request.signal })
        .then((response) => response.text())
        .then((responseText) => {
          // A newer change, or a full selection made meanwhile, has its own
          // request.
          if (request.signal.aborted || !this.isConnected || this.isComplete()) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.syncAvailability(html, unchosenIndexes);
          [this, ...this.other].forEach((el) => el.setDisabled());

          // The first option still to choose is judged against the options
          // chosen before it, so when none of its values can be bought, no
          // choice can: say so instead of asking for one.
          const values = unchosen.length ? Array.from(unchosen[0].querySelectorAll('[data-value-id]')) : [];
          const soldOut = values.length > 0 && values.every((el) => el.dataset.available === 'false');
          this.toggleAddButton(true, soldOut ? window.theme.variantStrings.soldOut : this.dataset.chooseLabel);

          [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`].forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);
            if (source && destination) destination.innerHTML = source.innerHTML;
          });

          const span = html.getElementById(`SelectedVariant-${this.dataset.section}`);
          if (span && span.dataset.featuredMediaId) {
            this.updateMedia({ featured_media: { id: parseInt(span.dataset.featuredMediaId, 10) } });
          }
        })
        .catch((e) => {
          // Only the gallery and price wait on this: the buy buttons keep
          // asking for a choice (onIncompleteChange), and nothing navigates.
          if (!isAbortError(e)) console.error(e);
        });
    }

    // Toggles inert on this section's dynamic checkout wrapper, which only
    // exists while "Require size selection" is waiting for a choice.
    setPaymentInert(inert) {
      document.querySelectorAll(`product-form[data-section="${this.dataset.section}"] .product-form__dynamic-checkout`).forEach((wrapper) => {
        wrapper.toggleAttribute('inert', inert);
      });
    }

    // "Require size selection": shows the SKU, inventory notice and pickup
    // availability once a variant is chosen (product-information.liquid).
    setSelectionPending(pending) {
      const info = document.getElementById(`ProductInfo-${this.dataset.section}`);
      if (info) info.toggleAttribute('data-selection-pending', pending);
    }

    // shopify:product:select — fires after every option change with the
    // resolved variant, or null when the chosen combination doesn't exist.
    publishProductSelect() {
      const data = this.getStandardEventData();
      if (!data) return;
      publishStandardEvent('ProductSelectEvent', {
        product: data.product,
        selectedOptions: data.selectedOptions,
        promise: Promise.resolve({ variant: data.variant })
      }, this);
    }

    // shopify:product:view, for the product this picker shows: fired by a
    // combined listing switch, which brings another product into view.
    publishProductView(context) {
      const data = this.getStandardEventData();
      if (!data) return;
      publishStandardEvent('ProductViewEvent', {
        context: context,
        selectedOptions: data.selectedOptions,
        product: Object.assign({}, data.product, { selectedVariant: data.variant })
      }, this);
    }

    // The product, its chosen options and the resolved variant (null when
    // the chosen combination doesn't exist), as standard events take them.
    getStandardEventData() {
      const productId = parseInt(this.dataset.productId, 10) || theme.settings.product_id;
      const selectedOptions = [];
      this.querySelectorAll('fieldset').forEach((fieldset) => {
        const select = fieldset.querySelector('select');
        const checked = fieldset.querySelector('input:checked');
        const value = select ? select.value : (checked ? checked.value : '');
        if (fieldset.dataset.optionName && value) {
          selectedOptions.push({ name: fieldset.dataset.optionName, value: value });
        }
      });
      if (!productId || !this.dataset.productHandle || !selectedOptions.length) return null;

      // "Require size selection" still waiting: the variant the server
      // put in the form isn't one the shopper chose.
      const variant = this.requireSelection && !this.isComplete() ? null : this.currentVariant;
      return {
        product: { id: productId, title: this.dataset.productTitle, handle: this.dataset.productHandle },
        selectedOptions: selectedOptions,
        variant: variant && variant.id ? {
          id: variant.id,
          title: variant.title || selectedOptions.map((option) => option.value).join(' / '),
          availableForSale: variant.available,
          price: { amount: standardEventAmount(variant.price), currencyCode: theme.settings.currency },
          selectedOptions: selectedOptions
        } : null
      };
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const productTemplate = productForm.closest('.product-form').getAttribute('template');
      const submitButtons = this.getAddButtons();

      if (!submitButtons) return;

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');

          // Editing a cart line, the choice takes the line's place.
          if (this.getLineEdit()) {
            submitButtonText.textContent = window.theme.variantStrings.update || 'Update';
          } else if (productTemplate?.includes('pre-order')) {
            submitButtonText.textContent = window.theme.variantStrings.preOrder;
          } else {
            submitButtonText.textContent = window.theme.variantStrings.addToCart;
          }
        }
      });

      if (!modifyClass) return;
    }

    setUnavailable() {
      const submitButtons = this.getAddButtons();
      const price = document.getElementById(`price-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        if (!submitButton) return;
        submitButtonText.textContent = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') return;

      this.fieldsets.forEach((fieldset) => {
        if (fieldset.querySelector('select')) {
          // Real values only: the "Choose options" placeholder stays disabled.
          fieldset.querySelectorAll('option[data-value-id]').forEach((opt) => {
            opt.disabled = opt.dataset.available === 'false';
          });
        } else {
          fieldset.querySelectorAll('input').forEach((input) => {
            input.classList.toggle('is-disabled', input.dataset.available === 'false');
          });
        }
      });
      return true;
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]').dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;
        this.setImageSetMedia();
      }
    }

    setImageSetMedia() {
      if (!this.imageSetIndex) {
        return;
      }

      const optionPosition = parseInt(this.imageSetIndex.replace('option', ''), 10) - 1;
      const fieldset = this.fieldsets[optionPosition];
      if (!fieldset) return;
      const select = fieldset.querySelector('select');
      const checked = fieldset.querySelector('input:checked');
      let setValue;
      if (select && select.selectedOptions[0]) {
        setValue = select.selectedOptions[0].dataset.handle;
      } else if (checked) {
        setValue = checked.dataset.handle;
      }
      if (!setValue) return;
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);
      if (this.hideVariants) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        const checked = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
        return checked ? checked.value : '';
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.pagination = this.parentElement.querySelector('.product-images-buttons');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      // Start Gallery
      let observer = new MutationObserver(() => {
        this.setupProductGallery();
      });

      observer.observe(this, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        characterData: false
      });

      this.setupProductGallery();

      // Start Pagination
      if (this.pagination) {
        this.setupPagination();
        this.resizeObserver = new ResizeObserver(entries => this.onPaginationResize());
        this.resizeObserver.observe(this);
        this.addEventListener('scroll', this.updatePagination.bind(this));
      }
    }
    disconnectedCallback() {
      if (this.onVariantChange) {
        document.removeEventListener('product:variant-change', this.onVariantChange);
        this.onVariantChange = null;
      }
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }

      this.setEventListeners();

      // Added once: the observer runs this again on every class or slide
      // change. Only this section's variant picker renames the gallery.
      if (!this.onVariantChange) {
        this.onVariantChange = (e) => {
          if (!e.detail || e.detail.sectionId !== this.getSectionId()) return;
          if (e.detail.variantTitle && e.detail.variantTitle !== 'Default Title') {
            this.dataset.variantTitle = e.detail.variantTitle;
            this.updatePswpProductInfo();
          }
        };
        document.addEventListener('product:variant-change', this.onVariantChange);
      }
    }
    // The section id this product's variant picker and form carry, which
    // product:variant-change names in e.detail.sectionId.
    getSectionId() {
      const detail = this.closest('.thb-product-detail'),
        owner = detail && detail.querySelector('variant-selects[data-section], product-form[data-section]');
      return owner ? owner.dataset.section : null;
    }
    buildItems(activeImages) {
      let images = activeImages.map((item) => {
        let activelink = item.querySelector('.product-single__media-zoom');
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          title: activelink.getAttribute('title')
        };
      });
      return images;
    }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) {
        container.parentElement.style.display = 'none';
        return;
      }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb';
        thumb.dataset.index = index;
        const img = document.createElement('img');
        img.src = item.thumbSrc;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') {
        infoEl.style.display = 'none';
        return;
      }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      const thumbs = container.querySelectorAll('.pswp__thumb');
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ inline: 'center', behavior: theme.reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
      }
    }
    setEventListeners() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide--image')).filter(element => element.clientWidth > 0);
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: true,
        allowPanToNext: true,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true
      };
      // Reduced motion: open and close without zooming from the thumbnail.
      if (theme.reducedMotion) {
        this.pswpOptions.showAnimationDuration = 0;
        this.pswpOptions.hideAnimationDuration = 0;
      }
      this.pswpOptions.errorMsg = this.pswpErrorMessage();

      // Each pass replaces the last one's listeners rather than adding to them.
      if (this.galleryController) this.galleryController.abort();
      this.galleryController = new AbortController();
      const signal = this.galleryController.signal;

      const _this = this;

      this.querySelectorAll('.product-single__media-zoom').forEach(function (link) {
        let thumbnail = link.closest('.product-single__media');
        let clone = link.cloneNode(true);
        thumbnail.append(clone);
        link.remove();
        clone.addEventListener('click', (e) => _this.zoomClick(e, clone), { signal });
      });

      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        }, { signal });
      });
    }
    // Shown in place of an image that fails to load. PhotoSwipe takes it
    // as markup and the string is decoded text, so it goes in as text.
    pswpErrorMessage() {
      const message = document.createElement('div');
      message.className = 'pswp__error-msg';
      message.textContent = (theme.strings && theme.strings.imageLoadError) || 'The image couldn\'t be loaded.';
      return message.outerHTML;
    }
    zoomClick(e, link) {
      let items = this.buildItems(this.activeImages);
      let parent = link.closest('.product-images__slide');
      let i = this.activeImages.indexOf(parent);
      this.pswpOptions.index = parseInt(i, 10);
      this.pswpOptions.getThumbBoundsFn = () => {
        const thumbnail = link.closest('.product-single__media'),
          pageYScroll = window.scrollY || document.documentElement.scrollTop,
          rect = thumbnail.getBoundingClientRect();
        return {
          x: rect.left,
          y: rect.top + pageYScroll,
          w: rect.width
        };
      };
      if (typeof PhotoSwipe !== 'undefined') {
        this.buildPswpThumbnails(items);
        this.updatePswpProductInfo();

        // Listeners on the shared .pswp markup last for this opening only.
        if (this.pswpController) this.pswpController.abort();
        const pswpController = new AbortController(),
          signal = pswpController.signal;
        this.pswpController = pswpController;

        const bottomBar = this.pswpElement.querySelector('.pswp__bottom-bar');
        if (bottomBar) {
          ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => {
            bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation(), { signal });
          });
        }

        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, items, this.pswpOptions);
        const _this = this;

        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 140
            };
          });
        });

        const scrollWrap = this.pswpElement.querySelector('.pswp__scroll-wrap');
        const resetScroll = function() {
          if (scrollWrap) scrollWrap.scrollLeft = 0;
          _this.pswpElement.scrollLeft = 0;
        };

        pswp.listen('beforeChange', function () {
          const index = pswp.getCurrentIndex();
          _this.syncPswpThumbnails(index);
          resetScroll();
        });

        const thumbsInner = this.pswpElement.querySelector('.pswp__thumbs-inner');
        if (thumbsInner) {
          thumbsInner.addEventListener('click', function handler(evt) {
            evt.stopPropagation();
            const thumb = evt.target.closest('.pswp__thumb');
            if (thumb) {
              pswp.goTo(parseInt(thumb.dataset.index, 10));
              resetScroll();
            }
          }, { signal });
        }

        pswp.listen('close', function () {
          const container = _this.pswpElement.querySelector('.pswp__thumbs-inner');
          if (container) container.innerHTML = '';
        });

        // PhotoSwipe moves focus into the gallery as it opens and leaves
        // it there; once closed, focus goes back to the zoom link that
        // opened it, which is where the gallery closes to.
        pswp.listen('destroy', function () {
          pswpController.abort();
          if (_this.pswpController === pswpController) _this.pswpController = null;
          if (link.isConnected) link.focus({ preventScroll: true });
        });

        pswp.init();
        this.syncPswpThumbnails(this.pswpOptions.index);
      }
      e.preventDefault();
    }
    setupPagination() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

      this.currentPageElement = this.pagination.querySelector('.slider-counter--current');
      this.pageTotalElement = this.pagination.querySelector('.slider-counter--total');

      this.prevButton = this.pagination.querySelector('button[name="previous"]');
      this.nextButton = this.pagination.querySelector('button[name="next"]');


      this.prevButton.addEventListener('click', this.onPaginationButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onPaginationButtonClick.bind(this));

      this.updatePagination();
    }
    onPaginationResize() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);

      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      this.updatePagination();
    }
    onPaginationButtonClick(event) {
      event.preventDefault();
      this.slideScrollPosition = event.currentTarget.name === 'next' ? this.scrollLeft + (1 * this.sliderItemOffset) : this.scrollLeft - (1 * this.sliderItemOffset);
      this.scrollTo({
        left: this.slideScrollPosition
      });
    }
    updatePagination() {
      if (!this.nextButton) return;

      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement) {
        this.currentPageElement.textContent = this.currentPage;
      }
      if (this.pageTotalElement) {
        this.pageTotalElement.textContent = this.sliderItemsToShow.length;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        }));
      }

      if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.scrollLeft === 0) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
    // Also right in RTL: offsetLeft ignores scrolling, and slides that
    // overflow to the left get negative offsets, matching the 0 to negative
    // scrollLeft range there (sliderItemOffset turns negative too).
    isSlideVisible(element, offset = 0) {
      const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
      return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  // A cart request's failure (theme.cart, app.js) in the shopper's words.
  const cartErrorMessage = (error) => {
    const description = error && error.body && error.body.description;
    return typeof description === 'string' && description ? description : theme.strings.cartError;
  };
  // Shows message on the cart line with this key where the cart is
  // (surface: 'drawer' or 'cart'), or above the lines when it has gone.
  // `html` is for the theme's own _html strings.
  const showCartLineError = (surface, key, message, html) => {
    const drawer = document.getElementById('Cart-Drawer');
    const page = document.getElementById('Cart');
    const target = surface === 'drawer' && drawer ? drawer : page || drawer;
    if (target && typeof target.showLineError === 'function') {
      target.showLineError(key, message, html);
    } else if (target && typeof target.displayErrors === 'function') {
      target.displayErrors(key, html ? new DOMParser().parseFromString(message, 'text/html').documentElement.textContent : message);
    }
  };
  // The Edit link (cart-line-edit.liquid) of the cart line with this key:
  // in the drawer while it is open and the edit began there, else on the
  // cart page.
  const findEditLink = (surface, key) => {
    const drawer = document.getElementById('Cart-Drawer');
    const page = document.getElementById('Cart');
    const scope = surface === 'drawer' && drawer && OverlayManager.isOpen(drawer) ? drawer : page || drawer;
    if (!scope) return null;
    return CartFocus.visible(scope.querySelectorAll(`.cart-item-edit[data-edit-key="${CSS.escape(key)}"] a[href]`))[0] || null;
  };
  // How many of a variant the cart shows where the edit began, on lines
  // other than the one with `key`: each line's title links to its variant
  // (item.url), and its .qty holds the rendered quantity.
  const shownQuantity = (surface, variantId, key) => {
    const drawer = document.getElementById('Cart-Drawer');
    const page = document.getElementById('Cart');
    const scope = surface === 'drawer' && drawer ? drawer : page || drawer;
    if (!scope) return 0;
    return Array.from(scope.querySelectorAll('[id^="CartDrawerItem-"], [id^="CartItem-"]')).reduce((sum, row) => {
      const input = row.querySelector('.qty');
      const link = row.querySelector('a.cart-product-link[href]');
      if (!input || !link || input.dataset.key === key) return sum;
      const variant = new URL(link.getAttribute('href'), window.location.origin).searchParams.get('variant');
      return variant === String(variantId) ? sum + (parseInt(input.defaultValue, 10) || 0) : sum;
    }, 0);
  };
  // The same line item properties, leaving out blank ones.
  const sameProperties = (a, b) => {
    const list = (properties) => Object.keys(properties || {}).filter((name) => properties[name] !== '' && properties[name] !== null).sort().map((name) => `${name}=${properties[name]}`).join('\n');
    return list(a) === list(b);
  };

  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.form.querySelector('[name=id]').disabled = false;
      if (!this.sticky) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      this.hideErrors = this.dataset.hideErrors === 'true';
      // Resolved while still attached: the quick-view drawer is torn down before
      // the add-to-cart response arrives, so closest() would miss it later.
      this.standardEventContext = this.closest('#Product-Drawer') ? 'dialog' : 'product';
    }
    onSubmitHandler(evt) {
      evt.preventDefault();
      // "Require size selection" keeps the id empty until every option is
      // chosen, so there is nothing to add yet.
      if (!this.form.querySelector('[name=id]').value) {
        return;
      }
      if (!this.form.reportValidity()) {
        return;
      }
      // This section's buttons only: the main form and its sticky bar.
      const submitButtons = document.querySelectorAll(`product-form[data-section="${this.dataset.section}"] .single-add-to-cart-button`);

      // Quick view editing a cart line replaces it (once at a time).
      const edit = this.getLineEdit();
      if (edit && this.replacing) return;

      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleErrorMessage();

      if (edit) {
        this.replacing = true;
        this.replaceLine(edit, new FormData(this.form))
          .catch((e) => console.error(e))
          .finally(() => {
            this.replacing = false;
            submitButtons.forEach((submitButton) => {
              submitButton.classList.remove('loading');
              submitButton.removeAttribute('aria-disabled');
            });
          });
        return;
      }

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };
      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
      // Its place among the cart's redraws (theme.cart.draws, app.js).
      const sequence = window.theme && theme.cart && theme.cart.claim ? theme.cart.claim() : 0;

      // A refused add (the Ajax API's error JSON, eg. a 422) says why; one
      // without a usable answer (eg. the bot check's 429 page, or no
      // connection) gives the generic error.
      const failed = (reply) => {
        dispatchCustomEvent('product:variant-error', {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: reply.description,
          message: reply.message
        });
        if (reply.status === 422) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
        }
        this.handleErrorMessage(cartErrorMessage({ body: reply }));
      };

      // themeFetch rejects an answer that isn't ok, without parsing one that
      // isn't JSON.
      themeFetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            failed(response);
            return;
          }

          this.renderContents(response, sequence);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response,
            quantity: parseInt(formData.get('quantity'), 10) || 1,
            context: this.standardEventContext
          });
        }, (error) => {
          if (!error.body) console.error(error);
          failed(error.body || {});
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    // The cart line quick view is editing (QuickView.editing, app.js), when
    // this form is in the product drawer; otherwise null.
    getLineEdit() {
      const quickView = customElements.get('quick-view');
      return quickView && quickView.editingFor ? quickView.editingFor(this) : null;
    }

    // Quick view editing a cart line: the form's choice takes the line's
    // place. With nothing changed, the drawer just closes; with only the
    // quantity, the line changes. Otherwise the choice is added, with the
    // line's properties (a property field filled in here wins), and then
    // the line is removed. A refused add (stock, quantity rules) says why
    // here and leaves the line as it was, unless Shopify added part of it
    // anyway (settleRefusedEdit); a removal that fails redraws the
    // cart and says the old line is still in it. theme.cart (app.js) sends
    // shopify:cart:error for any request that fails, and one add and one
    // change event go out for the lines that changed. Resolves when done.
    replaceLine(edit, formData) {
      const id = parseInt(formData.get('id'), 10);
      const quantity = parseInt(formData.get('quantity'), 10) || edit.quantity;
      const sellingPlan = formData.get('selling_plan');
      const properties = Object.assign({}, edit.properties);
      // The form's property fields, the last value of a name counting, as
      // in the cart (a checkbox comes after its hidden unchecked value).
      const entered = {};
      formData.forEach((value, name) => {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (match && typeof value === 'string') entered[match[1]] = value;
      });
      let propertiesChanged = false;
      Object.keys(entered).forEach((name) => {
        if (entered[name] === '' || entered[name] === properties[name]) return;
        properties[name] = entered[name];
        propertiesChanged = true;
      });

      if (id === edit.variantId && !propertiesChanged && !sellingPlan) {
        if (quantity === edit.quantity) {
          this.closeEdit(edit);
          return Promise.resolve();
        }
        this.markLine(edit, true);
        return theme.cart.change(edit.key, quantity).then((state) => {
          this.finishEdit(edit, state, edit.key);
          theme.cart.changed([{ key: edit.key, quantity: quantity }], state, 'dialog');
          // Accepted, but capped at what can be bought.
          const line = (state.items || []).find((item) => item.key === edit.key);
          if (line && line.quantity < quantity) showCartLineError(edit.surface, edit.key, theme.strings.cartQuantityError.replace('[quantity]', line.quantity), true);
        }, (error) => {
          this.markLine(edit, false);
          this.handleErrorMessage(cartErrorMessage(error));
          // Answered: the line can have moved anyway, capped at the stock.
          if (error.status) {
            theme.cart.refresh();
            if (error.status === 422) publishCappedCartLine(edit.key, edit.quantity, 'dialog');
          }
        });
      }

      const item = { id: id, quantity: quantity, properties: properties };
      if (sellingPlan) item.selling_plan = sellingPlan;
      // What the cart shows of the new choice already, for a refused add.
      const before = shownQuantity(edit.surface, id, edit.key);
      this.markLine(edit, true);
      return theme.cart.add(item, { sections: false }).then((added) => {
        const line = (added && Array.isArray(added.items) && added.items[0]) || {};
        return theme.cart.change(edit.key, 0).then((state) => {
          const items = state.items || [];
          const kept = items.find((entry) => entry.key === line.key) || items.find((entry) => entry.variant_id === id);
          this.finishEdit(edit, state, kept ? kept.key : line.key);
          theme.cart.added([item], added, 'dialog');
          theme.cart.changed([{ key: edit.key, quantity: 0 }], state, 'dialog');
        }, () => {
          theme.cart.added([item], added, 'dialog');
          return this.recoverEdit(edit, line.key);
        });
      }, (error) => {
        if (!error.status) {
          this.markLine(edit, false);
          this.handleErrorMessage(cartErrorMessage(error));
          return;
        }
        return theme.cart.get().then((cart) => cart, () => null).then((cart) => this.settleRefusedEdit(edit, item, before, cart, error));
      });
    }

    // A refused add that the server answered (a 422) can still have added
    // what the stock allows ("Only 50 items were added…"). When the cart now
    // holds more of the new choice than it showed (`before`), the edit goes
    // through with that: the old line is removed, the message shows on the
    // new line, and the add reports what went in. Otherwise the old line
    // stays, the message shows in the form, and the cart is redrawn.
    settleRefusedEdit(edit, item, before, cart, error) {
      const message = cartErrorMessage(error);
      const lines = cart && Array.isArray(cart.items) ? cart.items.filter((entry) => entry.variant_id === item.id && entry.key !== edit.key) : [];
      const now = lines.reduce((sum, entry) => sum + entry.quantity, 0);
      if (now <= before) {
        this.markLine(edit, false);
        this.handleErrorMessage(message);
        theme.cart.refresh();
        return Promise.resolve();
      }
      const line = lines.find((entry) => sameProperties(entry.properties, item.properties)) || lines[0];
      const requested = [Object.assign({}, item, { quantity: now - before })];
      return theme.cart.change(edit.key, 0).then((state) => {
        const kept = (state.items || []).find((entry) => entry.key === line.key) || line;
        this.finishEdit(edit, state, kept.key);
        theme.cart.added(requested, { items: [kept] }, 'dialog');
        theme.cart.changed([{ key: edit.key, quantity: 0 }], state, 'dialog');
        showCartLineError(edit.surface, kept.key, message);
      }, () => {
        theme.cart.added(requested, { items: [line] }, 'dialog');
        return this.recoverEdit(edit, line.key);
      });
    }

    // After an edit: the cart is redrawn from state where it is on the page
    // (the drawer stays open when the edit began there, and the cart page
    // doesn't open it), the product drawer closes, and focus goes to the
    // Edit link of the line key, now in the cart, unless the shopper moved
    // it out of the product drawer meanwhile.
    finishEdit(edit, state, key) {
      const productDrawer = document.getElementById('Product-Drawer');
      const active = document.activeElement;
      const focusLine = !!key && (!active || active === document.body || (!!productDrawer && productDrawer.contains(active)));
      theme.cart.render(state, focusLine ? { focusKey: key } : {});
      if (focusLine) {
        const link = findEditLink(edit.surface, key);
        if (link) link.focus();
      }
      this.closeEdit(edit);
    }

    // Closes the product drawer, unless it has gone on to show another
    // product meanwhile.
    closeEdit(edit) {
      const productDrawer = document.getElementById('Product-Drawer');
      const quickView = customElements.get('quick-view');
      if (!productDrawer || !quickView || quickView.editing !== edit || !OverlayManager.isOpen(productDrawer) || OverlayManager.closing.has(productDrawer)) return;
      OverlayManager.close(productDrawer);
    }

    // The new choice is in the cart, but the old line couldn't be taken
    // out: the cart is redrawn as it now is, saying so on the old line.
    recoverEdit(edit, key) {
      const message = theme.strings.cartEditPartialError || 'Your new choice was added, but the previous item couldn\'t be removed. Please remove it from your cart.';
      const sections = theme.cart.sections();
      const show = () => showCartLineError(edit.surface, edit.key, message);
      if (!sections.length) {
        this.closeEdit(edit);
        return Promise.resolve();
      }
      return themeFetch(`${window.location.pathname}?sections=${sections.join(',')}`)
        .then((response) => response.json())
        .then((rendered) => {
          this.finishEdit(edit, { sections: rendered }, key);
          show();
        }, (error) => {
          console.error(error);
          theme.cart.refresh();
          this.closeEdit(edit);
          show();
        });
    }

    // The edited line's row, shown loading while the edit is sent (a
    // redraw takes it back off).
    markLine(edit, loading) {
      const row = document.getElementById(edit.surface === 'drawer' ? `CartDrawerItem-${edit.index}` : `CartItem-${edit.index}`);
      if (row && CartFocus.lineKey(row) === edit.key) row.classList.toggle(edit.surface === 'drawer' ? 'thb-loading' : 'loading', loading);
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    // A newer cart redraw already drawn (theme.cart.draws) keeps the cart;
    // the drawers still change over.
    renderContents(parsedState, sequence) {
      if (!sequence || theme.cart.draws(sequence)) {
        this.getSectionsToRender().forEach((section => {
          if (!document.getElementById(section.id)) {
            return;
          }
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        }));
      }



      const productDrawer = document.getElementById('Product-Drawer');
      const cartDrawer = document.getElementById('Cart-Drawer');

      if (productDrawer && productDrawer.contains(this)) {
        OverlayManager.close(productDrawer);
        if (cartDrawer) {
          OverlayManager.open(cartDrawer);
          dispatchCustomEvent('cart-drawer:open');
        }
      } else if (cartDrawer) {
        OverlayManager.open(cartDrawer);
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });

  // What the cart holds of the selected variant (data-cart-quantity, with
  // data-variant-id): the quantity rule's "N in cart"
  // (product-add-to-cart.liquid) and the volume pricing tier
  // (quantity-pricing.js) follow the cart after an add here or in quick
  // view, and after a change in the drawer or on the cart page, which
  // carries the cart. A variant change re-renders both.
  const showCartQuantities = (cart, elements) => {
    elements.forEach((element) => {
      const quantity = cart.items.reduce((sum, item) => (String(item.variant_id) === element.dataset.variantId ? sum + item.quantity : sum), 0);
      element.dataset.cartQuantity = quantity;
      const inCart = element.querySelector('[data-in-cart]');
      if (inCart) {
        inCart.hidden = quantity === 0;
        const count = inCart.querySelector('span');
        if (count) count.textContent = quantity;
      }
      if (typeof element.update === 'function') element.update();
    });
  };
  const updateCartQuantities = (event) => {
    const elements = document.querySelectorAll('[data-cart-quantity][data-variant-id]');
    if (!elements.length) return;
    const cart = event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      showCartQuantities(cart, elements);
      return;
    }
    fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => showCartQuantities(cart, document.querySelectorAll('[data-cart-quantity][data-variant-id]')))
      .catch((error) => console.error(error));
  };
  document.addEventListener('cart:item-added', updateCartQuantities);
  document.addEventListener('line-item:change:end', updateCartQuantities);
}

/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();

      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
    }
    connectedCallback() {
      this.setupObservers();
      this.setupToggle();
    }
    setupToggle() {
      const button = this.querySelector('.product-add-to-cart-sticky--inner'),
        content = this.querySelector('.product-add-to-cart-sticky--content');

      if (this.animations_enabled) {
        const tl = gsap.timeline({
          reversed: true,
          paused: true,
          onStart: () => {
            button.classList.add('sticky-open');
          },
          onReverseComplete: () => {
            button.classList.remove('sticky-open');
          }
        });

        tl
          .set(content, {
            display: 'block',
            height: 'auto'
          }, 'start')
          .from(content, {
            height: 0,
            duration: 0.25
          }, 'start+=0.001');

        button.addEventListener('click', function () {
          if (tl.reversed()) {
            tl.play();
          } else {
            tl.reverse();
          }

          return false;
        });
        this._open = () => {
          tl.reversed(false).progress(1);
          button.classList.add('sticky-open');
        };
      } else {
        button.addEventListener('click', function () {
          content.classList.toggle('active');
          return false;
        });
        this._open = () => {
          content.classList.add('active');
        };
      }


    }
    // Opens the options at once: a combined listing switch put this bar in
    // place of the open one the shopper was choosing in (product.js).
    open() {
      if (this._open) this._open();
    }
    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (covers.includes(entry.target)) {
              if (entry.intersectionRatio > 0) {
                inView.add(entry.target);
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0) {
                inView.delete(entry.target);
                if (_this.formPassed && !inView.size) _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                if (!inView.size) _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = document.getElementById(`product-form-${this.dataset.section}`),
        // The bar hides while the footer or a quick order list
        // (bulk-order.liquid) is in view, so it never covers their buttons.
        covers = [document.getElementById('footer'), ...document.querySelectorAll('bulk-order')].filter(Boolean),
        inView = new Set();
      _this.formPassed = false;
      this._observer = observer;
      observer.observe(form);
      covers.forEach((element) => observer.observe(element));
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}
